/**
 * A ficha da prateleira: o `ShelfCard` de `lib/features/dashboard/presentation/
 * widgets/shelf_widgets.dart`.
 *
 * São 128 por 186 com raio 16, a capa cobrindo tudo, o degradê preto de quatro
 * paradas puxando o título por cima e o pontinho na cor da seção antes da
 * linha de baixo. Lá é um cartão só, usado pela estante, pela galeria e pelas
 * duas prateleiras do cinema; aqui também — os quatro usos dividem esta peça em
 * vez de cada prévia desenhar a sua.
 *
 * A cor sai de `--cor-secao`, que a `.secao` de fora põe: a ficha não recebe
 * cor nenhuma, e é isso que faz a mesma peça ficar âmbar entre os livros e
 * rosa-quartzo na galeria.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **`acima` não existe em lá.** É a atribuição que a prévia dos livros mostra
 *   por ficha — lá ela é o rótulo de uma sub-fileira inteira, que numa casa de
 *   N pessoas não cabe.
 * - **Sem `aoTocar` a ficha vira enfeite.** Na galeria e no cinema o toque de
 *   cada ficha vai para o mesmo lugar que o toque da seção, e aí a seção
 *   inteira é o link — um `<button>` dentro de um `<a>` é HTML inválido, então
 *   quem não recebe `aoTocar` sai como `<span>`, sem foco e sem clique.
 */

import type { NomeDoIcone } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './previas.css';

interface Props {
  /** O endereço da capa. Vazio cai no espaço reservado com o desenho. */
  capa?: string;
  /** O desenho do espaço reservado, o `accent.icon` de lá. */
  icone: NomeDoIcone;
  titulo: string;
  /** O rótulo miúdo em cima do título. */
  acima?: string;
  /** O `subtitle`, com o pontinho na frente. */
  abaixo?: string;
  /** O `topBadge` do canto de cima. */
  selo?: string;
  /** Sem isto a ficha sai como enfeite, para caber dentro de um link maior. */
  aoTocar?: () => void;
  /** Para quando o título não basta — a galeria, cujas fichas não têm nome. */
  rotulo?: string;
}

export function FichaDaEstante({
  capa,
  icone,
  titulo,
  acima,
  abaixo,
  selo,
  aoTocar,
  rotulo,
}: Props) {
  const miolo = (
    <>
      {capa ? (
        <img className="previa-ficha-capa" src={capa} alt="" loading="lazy" />
      ) : (
        <span className="previa-ficha-capa vazia" aria-hidden="true">
          <Icone nome={icone} tamanho={26} />
        </span>
      )}

      {selo ? <span className="previa-ficha-selo">{selo}</span> : null}

      {titulo || acima || abaixo ? (
        <span className="previa-ficha-dizeres">
          {acima ? <span className="previa-ficha-quem">{acima}</span> : null}
          {titulo ? <span className="previa-ficha-nome">{titulo}</span> : null}
          {abaixo ? (
            <span className="previa-ficha-linha">
              <span className="previa-ficha-ponto" aria-hidden="true" />
              {abaixo}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );

  if (!aoTocar) return <span className="previa-ficha enfeite">{miolo}</span>;

  return (
    <button className="previa-ficha" type="button" onClick={aoTocar} aria-label={rotulo}>
      {miolo}
    </button>
  );
}
